/**
 * Накопитель файлов, отложенных до отправки.
 *
 * Приложить файл можно только к тому, что уже существует, поэтому до
 * отправки комментария (или сохранения описания) файлы копятся здесь,
 * а грузит их вызывающий — получив идентификатор.
 *
 * Один и тот же набор собирают три способа: «скрепка», перетаскивание
 * и вставка из буфера, — правила отбора у них общие.
 */

import { type ClipboardEvent, useState } from "react";

import { filesFromClipboard } from "./clipboardFiles";

export interface AttachOptions {
  /** Значение accept для выбора файла. */
  accept: string;
  /** Причина отказа или null — проверяется при выборе, а не после отправки. */
  validate?: (file: File) => string | null;
  onRejected?: (file: File, reason: string) => void;
}

export interface PendingFilesTray {
  files: File[];
  add: (incoming: ArrayLike<File> | null) => void;
  remove: (index: number) => void;
  clear: () => void;
  /** Вешается на поле ввода: без него файл из буфера вставится именем. */
  onPaste: (event: ClipboardEvent<HTMLElement>) => void;
}

/** Без attach вставка и отбор выключены: право на файлы проверяется
 *  один раз здесь, а не в каждом обработчике. */
export function usePendingFiles(attach?: AttachOptions): PendingFilesTray {
  const [files, setFiles] = useState<File[]>([]);

  const add = (incoming: ArrayLike<File> | null) => {
    if (attach === undefined || incoming === null) return;
    const accepted: File[] = [];
    for (const file of Array.from(incoming)) {
      const problem = attach.validate?.(file) ?? null;
      if (problem === null) accepted.push(file);
      else attach.onRejected?.(file, problem);
    }
    if (accepted.length > 0) setFiles((current) => [...current, ...accepted]);
  };

  const onPaste = (event: ClipboardEvent<HTMLElement>) => {
    if (attach === undefined) return;
    const pasted = filesFromClipboard(event.clipboardData);
    if (pasted.length === 0) return;
    // Иначе браузер вдобавок вставит в текст имя или путь файла.
    event.preventDefault();
    add(pasted);
  };

  return {
    files,
    add,
    remove: (index) => setFiles((current) => current.filter((_, i) => i !== index)),
    clear: () => setFiles([]),
    onPaste,
  };
}
